<template>
  <b-card class="graphic-report__card w-100" :class="{ 'is-default-chart': isDefaultChart }">
    <b-container fluid>
      <b-row class="no-internal-print">
        <b-col
          cols="12"
          md="6"
          order="2"
          order-md="1"
        >
          <slot name="chart-header-content" />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="d-flex justify-content-center justify-content-md-end flex-wrap"
          order="1"
          order-md="2"
        >
          <slot name="chart-header-buttons" />
          <b-button
            v-if="shouldShowPrintButton"
            variant="outline-primary"
            class="my-2"
            @click="printReport"
          >
            {{ $t('buttons.print') }}
          </b-button>
        </b-col>
      </b-row>
      <slot name="chart-summary-row" />
      <b-row v-if="oscillographyChartType === oscillographyChartTypes.OSCILLOGRAPHY">
        <s-empty-list
          class="w-100"
          v-if="showEmpty"
        />
        <oscillography-chart
          v-else
          chart-id="oscillography"
          first-column-i18n-title="fields.oscillography.samples"
          :chart-data="chartData"
          :maneuver-data="maneuverData"
          :remove-outlier.sync="removeChartOutlier"
          :enable-multi-axis.sync="enableChartMultiAxis"
          :start-index-from-one="true"
          :loading="loading"
          :oscillography-chart-type="oscillographyChartType"
          :use-units-for-axes="useUnitsForAxes"
          :hide-maneuvers-section="hideManeuversSection"
          :hide-installation-and-company-columns="hideInstallationAndCompanyColumns"
          @toggle-outlier="getData"
          @toggle-multi-axis="getData"
        />
      </b-row>
      <b-row v-else-if="oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS">
        <s-empty-list class="w-100" v-if="showEmpty" />
        <oscillography-chart
          x-data-manager="dateTime"
          chart-id="oscillography"
          first-column-i18n-title="fields.oscillography.samples"
          :chart-data="diagnosticsChartData"
          :maneuver-data="maneuverData"
          :remove-outlier.sync="removeChartOutlier"
          :enable-multi-axis.sync="enableChartMultiAxis"
          :start-index-from-one="false"
          :loading="loading"
          :oscillography-chart-type="oscillographyChartTypes.DIAGNOSTICS"
          :use-units-for-axes="useUnitsForAxes"
          :hide-maneuvers-section="hideManeuversSection"
          :enable-maneuver-list-controls="enableManeuverListControls"
          :hide-installation-and-company-columns="hideInstallationAndCompanyColumns"
          :show-exceeded-points="showExceededPoints"
          :show-diagnostic-context="showDiagnosticContext"
          @toggle-outlier="getData"
          @toggle-maneuver="toggleManeuver"
          @toggle-all-maneuvers="toggleAllManeuvers"
        />
      </b-row>
    </b-container>
  </b-card>
</template>

<script>
import print from '@/services/print';
import OscillographyChart from '@/components/charts/oscillography/OscillographyChart';
import defaultChartMixin from '@/domain/mixins/defaultChartMixin';
import oscillographyChartTypes from '@/domain/enums/oscillographyChartTypes';

export default {
  props: {
    reportData: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isDefaultChart: {
      type: Boolean,
      default: false,
    },
    oscillographyChartType: {
      type: String,
      default: oscillographyChartTypes.OSCILLOGRAPHY,
    },
    hideManeuversSection: {
      type: Boolean,
      default: false,
    },
    enableManeuverListControls: {
      type: Boolean,
      default: false,
    },
    hideInstallationAndCompanyColumns: {
      type: Boolean,
      default: false,
    },
    hidePrintButton: {
      type: Boolean,
      default: false,
    },
    useUnitsForAxes: {
      type: Boolean,
      default: false,
    },
    showExceededPoints: {
      type: Boolean,
      default: false,
    },
    showDiagnosticContext: {
      type: Boolean,
      default: false,
    },
    initialChartFilters: {
      type: Object,
      default: null,
    },
  },
  components: {
    OscillographyChart,
  },
  mixins: [defaultChartMixin],
  data() {
    return {
      originalDiagnosticsChartData: [],
      activeManeuvers: [],
      diagnosticsManeuverKeys: [],
      oscillographyChartTypes,
      ...(this.initialChartFilters && {
        removeChartOutlier: this.initialChartFilters.removeOutlier,
        enableChartMultiAxis: this.initialChartFilters.multiAxis,
      }),
    };
  },
  computed: {
    shouldShowPrintButton() {
      return !this.hidePrintButton;
    },
    chartData() {
      const filteredData = this.reportData.chartData.filter(item => Array.isArray(item.values)
      && item.values.length > 0);
      const sortedChartData = ((this.reportData && filteredData) || [])
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }));

      return sortedChartData;
    },
    diagnosticsChartData() {
      if (this.activeManeuvers.length === 0) {
        return this.originalDiagnosticsChartData.map(item => ({
          ...item,
          values: [],
        }));
      }

      const activeManeuvers = new Set(this.activeManeuvers);
      return this.originalDiagnosticsChartData.map(item => ({
        ...item,
        values: item.values.filter(
          v => !v.maneuver || activeManeuvers.has(v.maneuver),
        ),
      }));
    },
    showEmpty() {
      if (this.loading) return false;

      const data = this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS
        ? this.diagnosticsChartData
        : this.chartData;

      return !data.some(item => Array.isArray(item.values) && item.values.length > 0);
    },
    maneuverData() {
      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
        return this.reportData.maneuvers.filter(maneuver => {
          if (!maneuver.infoData || !maneuver.infoData.diagnostics) {
            return false;
          }
          return maneuver.infoData.diagnostics.some(field => field.hasValue);
        });
      }
      return this.reportData.maneuvers;
    },
  },
  mounted() {
    if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
      this.syncDiagnosticsChartData(true);
    } else {
      this.activeManeuvers = this.reportData.maneuvers.map(this.getManeuverKey);
    }
  },
  watch: {
    reportData() {
      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
        this.syncDiagnosticsChartData();
      }
    },
  },
  methods: {
    syncDiagnosticsChartData(initializeManeuvers = false) {
      this.originalDiagnosticsChartData = (this.reportData.diagnosticsChartData || []).map(
        item => ({
          ...item,
          values: Array.isArray(item.values) ? [...item.values] : [],
        }),
      );

      const maneuverKeys = this.maneuverData.map(this.getManeuverKey);
      this.activeManeuvers = maneuverKeys.filter(key => initializeManeuvers
        || !this.diagnosticsManeuverKeys.includes(key)
        || this.activeManeuvers.includes(key));
      this.diagnosticsManeuverKeys = maneuverKeys;
    },
    getManeuverKey(maneuver) {
      return maneuver.maneuverKey || maneuver.maneuverUUID;
    },
    getData() {
      this.$emit('get-chart-data', this.chartFilters);
    },
    printReport() {
      print();
    },
    toggleManeuver(maneuverKey) {
      const index = this.activeManeuvers.indexOf(maneuverKey);
      if (index === -1) {
        this.activeManeuvers.push(maneuverKey);
      } else {
        this.activeManeuvers.splice(index, 1);
      }
    },
    toggleAllManeuvers(event) {
      if (event) {
        if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
          this.activeManeuvers = this.maneuverData.map(this.getManeuverKey);
        } else {
          this.activeManeuvers = this.reportData.maneuvers.map(this.getManeuverKey);
        }
      } else {
        this.activeManeuvers = [];
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .graphic-report
    &__card
      background-color: var(--color-background-light)

      &:not(.is-default-chart)
        padding-left: 0.5rem !important
        padding-right: 0.5rem !important
        padding-top: 0.5rem !important
        padding-bottom: 0.5rem !important
</style>
